import React from 'react';
import classnames from 'classnames';
import 'moment/locale/es';
import { groupEventsByCategory } from './calendarEventTypes';

// Deterministic bar count per cell (0-3), keyed off the day of month rather
// than random — so the grid reads like a real month (some cells empty, some
// with several events) instead of a uniform gray block, without reshuffling
// on every re-render.
const SKELETON_BAR_COUNTS = [1, 2, 0, 1, 3, 0, 2];

/**
 * Month-view day cell for the Inicio calendar redesign. Events are grouped
 * by category (see calendarEventTypes.groupEventsByCategory) instead of
 * listed individually — up to 3 category rows, "+N" overflow beyond that.
 * Overdue items keep a red ring on their category dot rather than a
 * separate hue, so the pending-vs-overdue signal survives the 9→4 color
 * consolidation.
 *
 * @param {moment.Moment} day
 * @param {Array} events - events for this day (any category)
 * @param {boolean} isCurrentMonth
 * @param {boolean} isToday
 * @param {boolean} isSelected
 * @param {Function} onClick - no-op for other-month/empty cells
 * @param {boolean} loading - true mientras se cargan los eventos del mes
 */
function CalendarDay({ day, events, isCurrentMonth, isToday, isSelected, onClick, loading }) {
	const categories = groupEventsByCategory(events);
	const visibleCategories = categories.slice(0, 3);
	const overflowCount = categories.length - visibleCategories.length;
	const skeletonBarCount = loading && isCurrentMonth ? SKELETON_BAR_COUNTS[day.date() % SKELETON_BAR_COUNTS.length] : 0;

	const handleClick = (e) => {
		e.stopPropagation();
		if (!isCurrentMonth) return;
		onClick();
	};

	return (
		<div
			className={classnames(
				'tw:min-h-[96px] tw:px-0.5 tw:py-1 tw:border-r tw:border-b tw:border-border tw:flex tw:flex-col tw:gap-1 tw:text-left tw:transition-colors tw:duration-100',
				isCurrentMonth ? 'tw:bg-surface tw:cursor-pointer tw:hover:bg-surface-2' : 'tw:bg-surface-2 tw:cursor-default',
				isSelected && 'tw:bg-accent-soft tw:hover:bg-accent-soft',
			)}
			onClick={handleClick}
		>
			<span
				className={classnames(
					'tw:w-[22px] tw:h-[22px] tw:rounded-token tw:flex tw:items-center tw:justify-center tw:text-[11.5px] tw:font-semibold',
					isToday ? 'tw:bg-accent tw:text-white' : isCurrentMonth ? 'tw:text-ink-1' : 'tw:text-ink-4',
				)}
			>
				{day.format('D')}
			</span>
			<div className='tw:flex tw:flex-col tw:gap-0.5'>
				{loading
					? Array.from({ length: skeletonBarCount }, (_, i) => (
						<span key={i} aria-hidden='true' className='el-sk tw:block tw:h-[8px] tw:w-full' />
					))
					: (
						<>
							{visibleCategories.map((cat) => (
								<div key={cat.category} className='tw:flex tw:items-center tw:gap-1 tw:text-[10.5px] tw:text-ink-1 tw:leading-tight tw:overflow-hidden'>
									<span
										className={classnames(
											// The overdue mark is a border, not a ring: a ring paints
											// outside the span's box and gets clipped by this row's
											// overflow-hidden (and the cell's 2px px-0.5), which reads
											// as a notch cut out of the dot. The box is always 12px
											// with a 2px border so overdue and non-overdue rows stay
											// aligned; bg-clip-padding keeps the fill off the border
											// area, leaving the same 8px colored core in both cases.
											'tw:w-3 tw:h-3 tw:border-2 tw:bg-clip-padding tw:rounded-[3px] tw:shrink-0 tw:box-border',
											cat.className,
											cat.hasOverdue ? 'tw:border-danger' : 'tw:border-transparent',
										)}
									/>
									<span className='tw:whitespace-nowrap tw:overflow-hidden tw:text-ellipsis'>
										{cat.label}{cat.count > 1 ? ` · ${cat.count}` : ''}
									</span>
								</div>
							))}
							{overflowCount > 0 && (
								<div className='tw:text-[10.5px] tw:text-ink-3 tw:ml-3'>+{overflowCount}</div>
							)}
						</>
					)}
			</div>
		</div>
	);
}

export default CalendarDay;
